// btw-front/src/components/admin/dashboard/DealReviewsTab.jsx
// [딜 심사] 탭 — 파트너가 올린 딜을 승인/반려한다. 승인해야 [딜] 탭·통계·실적에 편입된다.
//
// 하위 탭 2개: 심사 대기 / 처리 완료(승인·반려 한 목록, 최근 심사순).
//   대기는 오래 기다린 순으로 내려온다(서버 정렬) — 먼저 올린 파트너가 먼저 처리되도록.
//   처리 완료는 실제로 심사를 거친 건만 나온다(승인제 도입 이전 딜은 서버가 제외).
//
// 반려 사유는 필수다 — 파트너가 무엇을 고쳐야 할지 모르면 재심사를 요청할 수 없다.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal, message } from "antd";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { EmptyState } from "../../ui/EmptyState";
import { Input } from "../../ui/Input";
import { Textarea } from "../../ui/Textarea";
import { Tabs, formatKRW, stallTone } from "./shared";
import * as adminDealReviews from "../../../api/adminDealReviews";
import { filterByKeyword } from "../../../lib/keywordFilter";
import { useQueryState } from "../../../lib/useQueryState";
import { REVIEW_LIST_DONE, REVIEW_PENDING, REVIEW_REJECTED } from "../../../constants/dealReview";

// 처리 완료에는 **반려 딜도 함께** 나온다. 반려는 소프트 삭제를 동반하지만(=[삭제된 딜] 탭에도
// 있다) 심사 이력이기도 하므로, 반려 사유를 양쪽에서 확인할 수 있어야 한다 — 서버가 반려분만
// 삭제 여부를 보지 않고 내려준다(status=done). 승인 후 따로 삭제한 딜은 여기 없다.

// onPendingCountChange — 대시보드 탭 라벨의 대기 건수 배지를 갱신한다. 승인·반려 직후
// 배지가 그대로면 관리자가 "처리했는데 왜 숫자가 남아 있나" 로 혼란한다.
export default function DealReviewsTab({ onPendingCountChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sub, setSub] = useState(REVIEW_PENDING); // 'pending' | 'done'(승인+반려)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  // 키워드(파트너사·고객사·영업대표) — URL 에 보관해 딜 상세에서 돌아와도 유지.
  // 대기·처리 완료 하위 탭에 공통 적용. 대기 건수 배지는 검색과 무관하게 전체 건수다.
  const [q, setQ] = useQueryState("q");
  const visible = useMemo(
    () => filterByKeyword(items, q, ["partnerCompanyName", "customerCompany", "salesRepName"]),
    [items, q],
  );

  const load = useCallback(() => {
    setLoading(true);
    // 처리 완료는 승인+반려 묶음 — 서버가 최근 심사순으로 정렬해 내려준다.
    const req = adminDealReviews.list(sub === REVIEW_PENDING ? REVIEW_PENDING : REVIEW_LIST_DONE);
    return req
      .then((rows) => {
        setItems(rows);
        // 대기 목록을 방금 받아왔을 때만 배지를 갱신한다 — 처리 완료 목록의 건수로 덮어쓰면 안 된다.
        if (sub === REVIEW_PENDING && onPendingCountChange) onPendingCountChange(rows.length);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [sub, onPendingCountChange]);

  useEffect(() => {
    let cancelled = false;
    load().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [load, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  // 다른 관리자가 먼저 처리한 경우(409 ALREADY_REVIEWED)는 실패가 아니라 "이미 끝난 일" 이다 —
  // 오류로 겁주지 말고 안내 후 목록만 새로 고친다.
  function handleActionError(err) {
    if (err && err.code === "ALREADY_REVIEWED") {
      message.info(t("admin.dealReviews.alreadyReviewed"));
      refresh();
      return;
    }
    message.error((err && err.message) || t("admin.dealReviews.actionFailed"));
  }

  function handleApprove(d) {
    Modal.confirm({
      title: t("admin.dealReviews.confirmApprove.title"),
      content: (
        <div>
          <p>
            {t("admin.dealReviews.confirmApprove.body", {
              company: d.partnerCompanyName,
              customer: d.customerCompany,
            })}
          </p>
          <p className="mt-2 text-xs text-slate-500">
            {t("admin.dealReviews.confirmApprove.hint")}
          </p>
        </div>
      ),
      okText: t("admin.dealReviews.approve"),
      cancelText: t("partners.deals.detail.cancel"),
      onOk: () => {
        setActingId(d.id);
        return adminDealReviews
          .approve(d.id)
          .then(() => {
            message.success(t("admin.dealReviews.approved", { customer: d.customerCompany }));
            refresh();
          })
          .catch(handleActionError)
          .finally(() => setActingId(null));
      },
    });
  }

  function submitReject() {
    const reason = rejectReason.trim();
    if (!reason) {
      message.warning(t("admin.dealReviews.rejectReasonRequired"));
      return;
    }
    const d = rejectTarget;
    setActingId(d.id);
    adminDealReviews
      .reject(d.id, reason)
      .then(() => {
        message.success(t("admin.dealReviews.rejected", { customer: d.customerCompany }));
        setRejectTarget(null);
        setRejectReason("");
        refresh();
      })
      .catch(handleActionError)
      .finally(() => setActingId(null));
  }

  // 사유 전문 보기 — 처리 완료 목록에서 반려 사유는 길 수 있어 팝업으로 띄운다.
  function showReason(d) {
    Modal.info({
      title: t("admin.dealReviews.reasonTitle"),
      content: (
        <div className="text-sm">
          <p className="text-xs text-slate-500">
            {d.projectName} · {t("admin.dealReviews.reviewedAt")}: {d.reviewedAt || "-"} ·{" "}
            {t("admin.dealReviews.reviewedBy")}: {d.reviewedByName || "-"}
          </p>
          <p className="mt-3 whitespace-pre-wrap text-slate-800">{d.reviewRejectReason || "-"}</p>
        </div>
      ),
      okText: t("admin.deletedDeals.reasonClose"),
    });
  }

  const pending = sub === REVIEW_PENDING;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={sub}
          onChange={setSub}
          items={[
            { key: REVIEW_PENDING, label: t("admin.dealReviews.subPending") },
            { key: REVIEW_LIST_DONE, label: t("admin.dealReviews.subDone") },
          ]}
        />
        <Input
          name="search"
          placeholder={t("admin.dealReviews.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full sm:w-72"
        />
      </div>

      {/* 반려 건이 [삭제된 딜] 탭에도 있다는 사실을 알려 준다 — 양쪽에 보이는 이유의 설명 */}
      {!pending && (
        <p className="text-xs text-slate-500">{t("admin.dealReviews.rejectedMovedHint")}</p>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <EmptyState
            title={
              items.length > 0
                ? t("admin.dealReviews.searchEmpty")
                : pending
                  ? t("admin.dealReviews.emptyPending")
                  : t("admin.dealReviews.emptyDone")
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.dealReviews.submittedAt")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.dealReviews.company")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.dealReviews.projectName")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.dealReviews.customer")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                    {t("admin.dealReviews.poAmount")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.dealReviews.salesRep")}
                  </th>
                  {pending ? (
                    <>
                      <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                        {t("admin.dealReviews.daysWaiting")}
                      </th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                        {t("admin.dealReviews.action")}
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                        {t("admin.dealReviews.result")}
                      </th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                        {t("admin.dealReviews.reviewedAt")}
                      </th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                        {t("admin.dealReviews.reviewedBy")}
                      </th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                        {t("admin.dealReviews.reason")}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((d) => (
                  <tr
                    key={`${d.reviewStatus}-${d.id}`}
                    onClick={() => navigate(`/partners/deals/${d.id}`)}
                    className="cursor-pointer align-top transition hover:bg-slate-50"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                      {d.submittedAt || "-"}
                    </td>
                    <td className="break-words px-4 py-2.5 text-slate-700">
                      {d.partnerCompanyName || "-"}
                    </td>
                    <td className="break-words px-4 py-2.5 font-medium text-slate-900">
                      {d.projectName}
                      {/* 파트너가 관리자에게 남긴 메모 — 심사 판단 재료라 목록에서 바로 읽게 한다 */}
                      {d.partnerMemo && (
                        <p className="mt-1 whitespace-pre-wrap text-xs font-normal text-slate-500">
                          {d.partnerMemo}
                        </p>
                      )}
                    </td>
                    <td className="break-words px-4 py-2.5 text-slate-600">
                      {d.customerCompany}
                      <p className="mt-1 text-xs text-slate-400">
                        <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-slate-700">
                      {formatKRW(d.poAmount)}
                    </td>
                    {/* 영업대표가 딜을 올리는 주체 — 로그인 계정 이름(createdByName)은 목록에 따로 두지 않는다 */}
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                      {d.salesRepName || "-"}
                    </td>
                    {pending ? (
                      <>
                        <td
                          className={`whitespace-nowrap px-4 py-2.5 text-right font-medium ${stallTone(d.daysWaiting)}`}
                        >
                          {t("admin.dealReviews.days", { count: d.daysWaiting })}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right">
                          <div
                            className="flex justify-end gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              size="sm"
                              loading={actingId === d.id}
                              onClick={() => handleApprove(d)}
                            >
                              {t("admin.dealReviews.approve")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={actingId === d.id}
                              onClick={() => {
                                setRejectTarget(d);
                                setRejectReason("");
                              }}
                            >
                              {t("admin.dealReviews.reject")}
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <Badge tone={d.reviewStatus}>
                            {t(`partners.deals.review.${d.reviewStatus}`)}
                          </Badge>
                          {/* 반려 = 소프트 삭제 — 같은 딜이 [삭제된 딜] 탭에도 있는 이유를 행에서 밝힌다 */}
                          {d.reviewStatus === REVIEW_REJECTED && (
                            <p className="mt-1 text-xs text-slate-400">
                              {t("admin.dealReviews.rejectedDeletedNote")}
                            </p>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                          {d.reviewedAt || "-"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                          {d.reviewedByName || "-"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right">
                          {d.reviewRejectReason ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                showReason(d);
                              }}
                              className="text-xs text-accent underline-offset-2 hover:underline"
                            >
                              {t("admin.dealReviews.reasonView")}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        title={t("admin.dealReviews.confirmReject.title")}
        open={!!rejectTarget}
        onCancel={() => setRejectTarget(null)}
        onOk={submitReject}
        confirmLoading={!!rejectTarget && actingId === rejectTarget.id}
        okText={t("admin.dealReviews.reject")}
        cancelText={t("partners.deals.detail.cancel")}
        okButtonProps={{ danger: true }}
      >
        {rejectTarget && (
          <>
            <p className="mb-3 text-sm text-slate-600">
              {t("admin.dealReviews.confirmReject.body", {
                company: rejectTarget.partnerCompanyName,
                customer: rejectTarget.customerCompany,
              })}
            </p>
            <Textarea
              label={t("admin.dealReviews.rejectReasonLabel")}
              rows={4}
              maxLength={500}
              placeholder={t("admin.dealReviews.rejectReasonPlaceholder")}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            {/* 반려 기간에는 선점이 풀린다 — 관리자가 모르고 반려하면 고객사를 잃을 수 있다 */}
            <p className="mt-2 text-xs text-amber-700">
              {t("admin.dealReviews.confirmReject.preemptionWarning")}
            </p>
          </>
        )}
      </Modal>
    </section>
  );
}
